import type { ReactNode } from "react";
import styles from "./EmptyState.module.scss";

export interface EmptyStateProps {
  /** Headline copy (t3, fs-sm). Never filler; fresh accounts render THIS, not fake data (tenancy contract).*/
  title: string;
  description?: ReactNode;
  /** Optional CTA (e.g. a Button). */
  action?: ReactNode;
  /**
   * `default` is the centred block — right when the empty state IS the surface (a whole card, a page).
   *
   * `dropTarget` adds the dashed frame (the Top-8 tile).
   *
   * `inline` is the QUIET ONE-LINER, about as tall as the row it stands in for: title and description read
   * left-aligned on a single line. Use it inside a dense editor section, where the centred block's
   * `space-10` of block padding punches a hole the list it replaces would never leave — a section reads as
   * broken rather than empty.
   */
  variant?: "default" | "dropTarget" | "inline";
}

const BOX = { default: styles.empty, dropTarget: styles.dropTarget, inline: styles.inline } as const;

/**
 * The empty state. Every list/card ships one — the born-blank tenancy contract
 * a fresh band renders designed empty states, never placeholder content.
 */
export function EmptyState({ title, description, action, variant = "default" }: EmptyStateProps) {
  return (
    <div className={BOX[variant]}>
      <p className={styles.title}>{title}</p>
      {description && <p className={styles.description}>{description}</p>}
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}
